import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  ControlContainer,
  UntypedFormGroup,
  FormGroupDirective,
} from "@angular/forms";
import { forkJoin } from "rxjs";
import { map } from "rxjs/operators";
import { DomSanitizer } from "@angular/platform-browser";
import {
  WorkflowStatus,
  WorkflowStatusChange,
} from "src/app/shared/models/workflow";
import { LookupService } from "src/app/shared/services/lookup.service";
import { NotificationService } from "src/app/shared/services/notification.service";

@Component({
  selector: "workflow-dropdown",
  templateUrl: "./workflow-dropdown.component.html",
  styleUrls: ["./workflow-dropdown.component.scss"],
  viewProviders: [
    { provide: ControlContainer, useExisting: FormGroupDirective },
  ],
  standalone: false,
})
export class WorkflowDropdownComponent implements OnInit, OnChanges {
  @Input() recordType: string; //this will be the type of record: 'survey', 'cap', 'cap-action', 'dr'
  @Input() recordId: string; //this will be the Guid (if exists for the the record)
  @Input() statusChangeControlName: string;
  @Input() statusControlName: string;
  @Input() currentStatus: WorkflowStatus;
  @Input() statusChanges: any[];
  @Input() workflowStatuses: WorkflowStatus[];
  @Input() headerText: string;
  @Output() statusChangeSelected = new EventEmitter<any>();
  @Output() saveRequested = new EventEmitter<void>();

  selectedWorkflowStatusId: string = "";
  displayCurrentStatus: { buttonText: string; id: string };
  originalStatusId: string = "";

  get form(): UntypedFormGroup {
    return this.formGroupDirective.form;
  }

  constructor(
    private lookupService: LookupService,
    private readonly formGroupDirective: FormGroupDirective,
    private notificationService: NotificationService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.loadData(this.recordId);
    this.originalStatusId = this.currentStatus?.id || "";
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-sync displayCurrentStatus when the currentStatus input changes (e.g. after
    // a save round-trip refreshes the parent). The kendo dropdown's label reads from
    // displayCurrentStatus.buttonText, so without this it would stay frozen on the
    // pre-save value.
    if (changes["currentStatus"] && this.currentStatus) {
      this.displayCurrentStatus = {
        buttonText: this.currentStatus.name,
        id: this.currentStatus.id,
      };
      const newStatusId = this.currentStatus.id || "";
      if (newStatusId && newStatusId !== this.originalStatusId) {
        this.originalStatusId = newStatusId;
      }
    } else if (changes["recordId"]) {
      this.loadData(this.recordId);
    }
  }

  loadData(id: string) {
    if (this.recordType === "cap") {
      if (id === "") {
        this.currentStatus = this.workflowStatuses.find(
          (ii) => ii.code === "draft"
        );
      }
      //set the status to show in the ddl
      this.displayCurrentStatus = {
        buttonText: this.currentStatus.name,
        id: this.currentStatus.id,
      };

      this.lookupService.getCapDraftWorkflowStatusChanges().subscribe({
        next: (result) => {
          if (id === "") {
            this.statusChanges = result; //only need to set this when new item. When editing, the allowed status changes are passed in
          }
        },
        error: (err) => console.log("loadDataforWorkFlows", err),
      });
    } else if (this.recordType === "cap-action") {
      if (id === "") {
        this.currentStatus = this.workflowStatuses.find(
          (ii) => ii.code === "not-started"
        );
      }
      //set the status to show in the ddl
      this.displayCurrentStatus = {
        buttonText: this.currentStatus.name,
        id: this.currentStatus.id,
      };

      this.lookupService
        .getCapActionNotStartedWorkflowStatusChanges()
        .subscribe({
          next: (result) => {
            if (id === "") {
              this.statusChanges = result; //only need to set this when new item. When editing, the allowed status changes are passed in
            }
          },
          error: (err) => console.log("loadDataforWorkFlows", err),
        });
    } else if (this.recordType === "survey") {
    } else if (
      this.recordType === "personnel-status-au" ||
      this.recordType === "personnel-status-record"
    ) {
    } else if (this.recordType === "gifted-fiscal") {
      this.displayCurrentStatus = {
        buttonText: this.currentStatus.name,
        id: this.currentStatus.id,
      };
    } else if (this.recordType === "dr") {
      //set the status to show in the ddl
      this.displayCurrentStatus = {
        buttonText: this.currentStatus.name,
        id: this.currentStatus.id,
      };
    }
  }

  //if we need to disable an item...
  public itemDisabled(itemArgs: { dataItem: string; index: number }) {
    return itemArgs.index === -1;
  }

  onStatusChange(selectedStatusId: any) {
    // For cap and cap-action: confirm the change in a modal, then auto-save on Yes.
    // For all other record types (dr, survey, etc.) preserve the legacy behavior of
    // emitting statusChangeSelected so the parent component can drive the change.
    if (this.recordType !== "cap" && this.recordType !== "cap-action") {
      this.statusChangeSelected.emit(selectedStatusId);
      return;
    }

    if (selectedStatusId === this.originalStatusId) {
      return;
    }

    const currentStatusName = this.currentStatus?.name || "Unknown";
    const newStatus = this.statusChanges?.find(
      (s) => s.id === selectedStatusId
    );
    const newStatusName = newStatus?.buttonText || "Unknown";

    const sanitizedCurrentStatusName =
      this.sanitizer.sanitize(1, currentStatusName) || "Unknown";
    const sanitizedNewStatusName =
      this.sanitizer.sanitize(1, newStatusName) || "Unknown";

    const message = `Are you sure you want to change the Status from "<strong><em>${sanitizedCurrentStatusName}</em></strong>" to "<strong><em>${sanitizedNewStatusName}</em></strong>"?`;

    this.notificationService
      .confirmationPromise(
        message,
        "Confirm Status Change",
        "Yes",
        "Cancel"
      )
      .then((result) => {
        if (result) {
          this.saveRequested.emit();
          this.originalStatusId = selectedStatusId;
        } else {
          this.form
            .get(this.statusChangeControlName)
            ?.setValue(this.originalStatusId);
        }
      });
  }
}
